import type { SessionResponse } from '@egr/gatekeeper-lib';

import { openSession } from '@egr/gatekeeper-lib';
import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';

import { ListenableEvent } from '@easterngraphics/wcf/modules/utils';
import { Lock } from '@easterngraphics/wcf/modules/utils/async';

// should be the same as SessionResponse
export interface SessionInformation {
    session: SessionResponse | undefined;
}

export const enum NewSessionReason {
    USER_CHANGED = 'user changed',
    GATEKEEPER_ID_CHANGED = 'gatekeeper id changed',
    SESSION_EXPIRED = 'session expired',
}

export const onNewSession = new ListenableEvent<NewSessionReason, undefined>(undefined);

export class EaiwsSessionManager extends SyncExternalStore<SessionInformation> {
    private static getInitialState() {
        return {
            session: undefined,
        };
    }

    private readonly lock = new Lock();

    public constructor(protected readonly applicationId: string, protected readonly onError: (error: unknown) => void) {
        super(EaiwsSessionManager.getInitialState());

        this.initializeSession = this.initializeSession.bind(this);

        onNewSession.addListener(async () => {
            const locked = await this.lock.acquireLock();
            try {
                this.updateState(EaiwsSessionManager.getInitialState());
            } finally {
                locked.unlock();
            }
        });
    }

    public async initializeSession(gatekeeperId: string, accessToken?: string, skipSession?: boolean) {
        const locked = await this.lock.acquireLock();

        if (skipSession && this.snapshot.session?.sessionId != null) {
            return;
        }
        try {
            const result = await openSession(gatekeeperId, {
                applicationId: this.applicationId,
                userToken: accessToken,
            });
            this.updateState({ session: result });
        } catch (error) {
            this.onError(error);
        } finally {
            locked.unlock();
        }
    }

    public getSession(): SessionResponse | undefined {
        const session = this.getSnapshot();
        if (session != null) {
            return session.session;
        }
        return undefined;
    }
}
